package floris.graphics.backoffice.pages.admin.techstacks

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.aspectRatio
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import floris.graphics.backoffice.components.layouts.PageLayout
import floris.graphics.core.constants.Res
import floris.graphics.core.navigation.Navigation
import com.materialdesignsystem.components.widgets.DsCard
import com.materialdesignsystem.components.widgets.DsEmptyScreen
import com.materialdesignsystem.components.widgets.DsSpinner
import com.materialdesignsystem.components.widgets.SpinnerSize
import com.materialdesignsystem.extensions.maxLines
import com.materialdesignsystem.toColorScheme
import floris.graphics.models.domain.models.Techstack
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A

@Page("/admin/techstacks")
@Composable
fun TechstacksPage() {
    val context = rememberPageContext()

    val viewModel = remember { TechstacksViewModel() }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.eventFlow.collectLatest {
            when (it) {
                is TechstacksUiEvent.Navigate -> context.router.navigateTo(it.route)
            }
        }
    }

    TechstacksContent(
        state = state,
        onEvent = viewModel::onEvent
    )
}

@Composable
private fun TechstacksContent(
    state: TechstacksState,
    onEvent: (TechstacksEvent) -> Unit
) {
    val breakpoint = rememberBreakpoint()

    PageLayout(
        title = "Techstacks",
        fabOnClick = {
            onEvent(TechstacksEvent.Navigate(Navigation.Screen.Admin.Techstacks.Manage().route))
        }
    ) {
        if (state.isLoading) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                DsSpinner(size = SpinnerSize.Large)
            }

            return@PageLayout
        }

        if (!state.isLoading && state.techstacks.isEmpty()) {
            DsEmptyScreen(
                title = "Geen techstacks gevonden",
                description = "Maak jou eerste techstack aan om deze daarna hier te vinden!",
                actionButtonText = "Voeg een nieuwe techstack toe",
                onActionButtonClick = { onEvent(TechstacksEvent.Navigate(Navigation.Screen.Admin.Techstacks.Manage().route)) }
            )

            return@PageLayout
        }

        SimpleGrid(
            modifier = Modifier
                .fillMaxWidth(if (breakpoint > Breakpoint.MD) 80.percent else 90.percent)
                .padding(top = if (breakpoint >= Breakpoint.MD) 58.px else 36.px)
                .gap(
                    rowGap = if (breakpoint > Breakpoint.MD) 36.px else 24.px,
                    columnGap = if (breakpoint > Breakpoint.MD) 36.px else 24.px
                ),
            numColumns = numColumns(1, 2, 3, 3, 4)
        ) {
            state.techstacks
                .flatMap { listOf(it) + it.subTechstacks.orEmpty() }
                .forEach {
                    TechstackCard(it)
                }
        }
    }
}

@Composable
fun TechstackCard(
    techstack: Techstack
) {
    val colorScheme = ColorMode.current.toColorScheme

    A(
        href = Navigation.Screen.Admin.Techstacks.Manage(techstack.id).route,
        attrs = Modifier
            .textDecorationLine(TextDecorationLine.None)
            .toAttrs()
    ) {
        DsCard(
            backgroundColor = colorScheme.secondaryContainer,
            contentColor = colorScheme.onSecondaryContainer,
            contentPadding = 12.px,
            content = {
                TechstackCardContent(techstack)
            }
        )
    }
}


@Suppress("MagicNumber")
@Composable
private fun TechstackCardContent(
    techstack: Techstack,
    titleMaxLength: Int = 2,
) {
    val colorScheme = ColorMode.current.toColorScheme

    Image(
        modifier = Modifier
            .fillMaxWidth()
            .margin(top = 10.px)
            .aspectRatio(4, 2)
            .objectFit(ObjectFit.Contain)
            .attrsModifier {
                attr("onerror", "this.onerror=null; this.src='${Res.Image.logo}'")
            },
        src = techstack.logoMediaFile?.url?.toString() ?: Res.Image.logo, // TODO [#28]: create fallback image!!
        description = "Techstack Thumbnail Image"
    )

    SpanText(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 10.px)
            .color(colorScheme.onBackground)
            .fontSize(20.px)
            .fontWeight(FontWeight.Bold)
            .textOverflow(TextOverflow.Ellipsis)
            .overflow(Overflow.Hidden)
            .textAlign(TextAlign.Center)
            .maxLines(titleMaxLength),
        text = techstack.name
    )
}
